/** One complete colour set. The widget renders a light and a dark variant. */
export interface WidgetPalette {
  primaryColor: string;
  headerTextColor: string;
  botBubbleColor: string;
  botTextColor: string;
  userTextColor: string;
  /** Panel and composer background. */
  surfaceColor: string;
  /** Message area background, usually the same as or near the surface. */
  panelColor: string;
  /** Body text outside bubbles, plus icon colours. */
  textColor: string;
  /** Hairlines: the composer divider and panel border. */
  borderColor: string;
  /** Composer input background. */
  inputColor: string;
}

/**
 * How the widget picks between the two palettes.
 * - light / dark: always that palette.
 * - auto: follows the host page, via prefers-color-scheme.
 */
export type ThemeMode = "light" | "dark" | "auto";

export const LIGHT_PALETTE: WidgetPalette = {
  primaryColor: "#075CE5",
  headerTextColor: "#FFFFFF",
  botBubbleColor: "#F1F1F3",
  botTextColor: "#18181B",
  userTextColor: "#FFFFFF",
  surfaceColor: "#FFFFFF",
  panelColor: "#FFFFFF",
  textColor: "#18181B",
  borderColor: "#ECECEC",
  inputColor: "#F1F1F3",
};

export const DARK_PALETTE: WidgetPalette = {
  primaryColor: "#3B82F6",
  headerTextColor: "#FFFFFF",
  botBubbleColor: "#27272A",
  botTextColor: "#FAFAFA",
  userTextColor: "#FFFFFF",
  surfaceColor: "#18181B",
  panelColor: "#18181B",
  textColor: "#FAFAFA",
  borderColor: "#27272A",
  inputColor: "#27272A",
};

/** Emits the custom properties for one palette. */
function paletteVars(p: WidgetPalette): string {
  return `
      --maicb-primary: ${p.primaryColor};
      --maicb-header-text: ${p.headerTextColor};
      --maicb-bot-bubble: ${p.botBubbleColor};
      --maicb-bot-text: ${p.botTextColor};
      --maicb-user-text: ${p.userTextColor};
      --maicb-surface: ${p.surfaceColor};
      --maicb-panel: ${p.panelColor};
      --maicb-text: ${p.textColor};
      --maicb-border: ${p.borderColor};
      --maicb-input: ${p.inputColor};`;
}

/** Kept for callers that still pass the old flat shape. */
export type WidgetTheme = Partial<WidgetPalette>;

/**
 * Rewrites selectors and custom properties written against the retired
 * "aicb" prefix to the current "maicb" one, so a business's saved Custom CSS
 * carries on working after the rename without them touching it.
 * `.aicb-root`, `--aicb-primary` and `aicb-open` all become their maicb
 * equivalents; anything already on the new prefix is left alone.
 */
export function upgradeLegacyCss(css: string | null): string | null {
  if (!css) return css;
  return css.replace(/(?<![A-Za-z0-9_])aicb([-_])/g, "maicb$1");
}

export function buildStyles(
  primaryColor: string,
  isLeft: boolean,
  fontFamily: string = "inherit",
  customCss: string | null = null,
  theme: Partial<WidgetPalette> = {},
  darkTheme: Partial<WidgetPalette> = {},
  themeMode: ThemeMode = "light",
): string {
  // The primary colour is still passed separately by older callers, so it
  // seeds both palettes unless a palette overrides it explicitly.
  const light: WidgetPalette = {
    ...LIGHT_PALETTE,
    primaryColor,
    ...theme,
  };
  const dark: WidgetPalette = {
    ...DARK_PALETTE,
    primaryColor,
    ...darkTheme,
  };
  const side = isLeft ? "left" : "right";
  const resolvedFontFamily = fontFamily === "inherit" 
    ? "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif" 
    : fontFamily;
  
  return `
    :host, .maicb-root {
      all: initial;
      font-family: ${resolvedFontFamily};
      color-scheme: ${themeMode === "auto" ? "light dark" : themeMode};

      /* Spacing from the screen edge. Override in Custom CSS, e.g.
         .maicb-root { --maicb-offset-side: 40px; --maicb-offset-bottom: 32px; }
         The mobile breakpoints below set their own values, so a desktop
         override cannot push the widget off a small screen. */
      --maicb-offset-side: 20px;
      --maicb-offset-bottom: 20px;

      /* Every colour below is a variable, so Custom CSS can override any one
         of them without restating the rule it belongs to. */
${paletteVars(themeMode === "dark" ? dark : light)}
    }
${
  themeMode === "auto"
    ? `
    /* auto: follow the host page's colour scheme. */
    @media (prefers-color-scheme: dark) {
      :host, .maicb-root {
${paletteVars(dark)}
      }
    }`
    : ""
}
    * { box-sizing: border-box; }

    .maicb-greeting {
      position: fixed;
      /* Clears the 56px launcher plus a gap, using the same offset anchors. */
      bottom: calc(var(--maicb-offset-bottom, 20px) + 68px);
      ${side}: var(--maicb-offset-side, 20px);
      z-index: 2147483000;
      display: flex;
      align-items: flex-start;
      gap: 6px;
      max-width: 260px;
      padding: 10px 12px;
      border-radius: 14px;
      border: 1px solid var(--maicb-border);
      background: var(--maicb-surface);
      box-shadow: 0 6px 20px rgba(0,0,0,0.16);
      animation: maicb-greeting-in 260ms ease-out;
    }
    .maicb-greeting[hidden] { display: none; }
    @keyframes maicb-greeting-in {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .maicb-greeting-text {
      font: inherit;
      font-size: 14px;
      line-height: 1.5;
      text-align: start;
      border: none;
      background: transparent;
      color: var(--maicb-text);
      padding: 0;
      cursor: pointer;
    }
    .maicb-greeting-close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      margin-top: 1px;
      padding: 0;
      border: none;
      border-radius: 4px;
      background: transparent;
      color: var(--maicb-text);
      opacity: 0.45;
      cursor: pointer;
    }
    .maicb-greeting-close:hover { opacity: 0.9; }
    .maicb-greeting-close svg { width: 12px; height: 12px; }

    .maicb-launcher {
      position: fixed;
      bottom: var(--maicb-offset-bottom, 20px);
      ${side}: var(--maicb-offset-side, 20px);
      width: 56px;
      height: 56px;
      border-radius: 9999px;
      background-color: var(--maicb-primary);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 8px 24px rgba(0,0,0,0.2);
      z-index: 2147483000;
      border: none;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .maicb-launcher:hover {
      transform: scale(1.05);
      box-shadow: 0 12px 32px rgba(0,0,0,0.3);
    }
    .maicb-launcher:active {
      transform: scale(0.95);
    }
    .maicb-launcher svg { width: 26px; height: 26px; }
    .maicb-launcher-icon { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; }

    .maicb-panel {
      position: fixed;
      /* Clears the launcher: its offset plus height and gap. */
      bottom: calc(var(--maicb-offset-bottom, 20px) + 68px);
      ${side}: var(--maicb-offset-side, 20px);
      width: 384px;
      height: 600px;
      max-height: calc(100vh - 120px);
      display: none;
      flex-direction: column;
      overflow: hidden;
      border-radius: 16px;
      background: var(--maicb-surface);
      box-shadow: 0 16px 48px rgba(0,0,0,0.24);
      z-index: 2147483000;
      border: 1px solid var(--maicb-border);
      transition: all 0.3s ease;
    }
    .maicb-panel.maicb-open { display: flex; }

    .maicb-header {
      --maicb-header-bg: var(--maicb-primary);
      color: var(--maicb-header-text);
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 14px 16px;
      font-size: 14px;
      font-weight: 500;
      background-color: var(--maicb-primary);
      flex-shrink: 0;
    }
    .maicb-header svg { width: 16px; height: 16px; }
    .maicb-minimize {
      /* Pushed to the far edge; margin-inline-start flips with RTL. */
      margin-inline-start: auto;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      border: none;
      border-radius: 6px;
      background: transparent;
      color: inherit;
      opacity: 0.75;
      cursor: pointer;
      flex-shrink: 0;
      transition: opacity 0.15s ease, background 0.15s ease;
    }
    .maicb-minimize:hover {
      opacity: 1;
      background: rgba(255, 255, 255, 0.15);
    }
    .maicb-minimize:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 1px;
    }
    .maicb-header-title {
      font-weight: 600;
      font-size: 14px;
      line-height: 1.2;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .maicb-logo { width: 16px; height: 16px; object-fit: contain; }
    .maicb-logo-mark { width: 20px; height: 20px; display: inline-flex; flex-shrink: 0; }
    .maicb-logo-mark svg { width: 100%; height: 100%; display: block; }

    .maicb-messages {
      flex: 1;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 12px;
      background: var(--maicb-panel);
    }

    .maicb-bubble {
      max-width: 85%;
      padding: 11px 15px;
      border-radius: 16px;
      font-size: 15px;
      /* 1.55 rather than 1.4: Arabic and Chinese need more vertical room
         than Latin script at the same nominal size. */
      line-height: 1.55;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .maicb-bubble-assistant {
      align-self: flex-start;
      background: var(--maicb-bot-bubble);
      color: var(--maicb-bot-text);
      border-bottom-left-radius: 4px;
    }
    .maicb-bubble-user {
      align-self: flex-end;
      background-color: var(--maicb-primary);
      color: var(--maicb-user-text);
      border-bottom-right-radius: 4px;
    }
    .maicb-bubble-error {
      align-self: flex-start;
      background: #fee2e2;
      color: #991b1b;
    }

    .maicb-prechat {
      /* Fills the whole messages area nothing else has rendered yet, so a
         small floating card there just leaves the rest of the panel empty. */
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 10px;
      margin: 0;
      padding: 24px;
      border-radius: 12px;
      border: 1px solid var(--maicb-border);
      background: var(--maicb-bot-bubble);
      box-sizing: border-box;
    }
    .maicb-prechat-title {
      font-size: 15px;
      font-weight: 600;
      color: var(--maicb-bot-text);
    }
    .maicb-prechat-message {
      font-size: 14px;
      line-height: 1.55;
      color: var(--maicb-bot-text);
      opacity: 0.75;
    }
    .maicb-prechat-input {
      font: inherit;
      /* 16px for the same reason as .maicb-input: below it, iOS zooms. */
      font-size: 16px;
      padding: 11px 13px;
      border-radius: 8px;
      border: 1px solid var(--maicb-border);
      background: var(--maicb-surface);
      color: var(--maicb-text);
      width: 100%;
      box-sizing: border-box;
    }
    .maicb-prechat-input:focus {
      outline: none;
      border-color: var(--maicb-primary);
    }
    .maicb-prechat-row {
      display: flex;
      gap: 8px;
      align-items: center;
    }
    .maicb-prechat-submit {
      font: inherit;
      font-size: 14px;
      font-weight: 500;
      padding: 11px 20px;
      border-radius: 8px;
      border: none;
      background: var(--maicb-primary);
      color: var(--maicb-user-text);
      cursor: pointer;
    }
    .maicb-prechat-submit:disabled { opacity: 0.6; cursor: default; }
    .maicb-prechat-skip {
      font: inherit;
      font-size: 14px;
      padding: 11px 14px;
      border: none;
      border-radius: 8px;
      background: transparent;
      color: var(--maicb-text);
      opacity: 0.6;
      cursor: pointer;
    }
    .maicb-prechat-skip:hover { opacity: 1; }

    .maicb-handoff-offer {
  display: flex;
  justify-content: flex-start;
  margin: 2px 0 8px 12px;
}
.maicb-handoff-btn {
  font: inherit;
  font-size: 14px;
  padding: 9px 16px;
  border-radius: 16px;
  border: 1px solid var(--maicb-primary);
  background: transparent;
  color: var(--maicb-primary);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.maicb-handoff-btn:hover {
  background: var(--maicb-primary);
  color: #fff;
}
.maicb-handoff-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 4px 12px 10px 12px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(127, 127, 127, 0.25);
}
.maicb-handoff-prompt {
  font-size: 14px;
  opacity: 0.8;
}
.maicb-handoff-input {
  font: inherit;
  /* 16px: iOS zoom threshold, same as every other focusable input. */
  font-size: 16px;
  padding: 11px 13px;
  border-radius: 8px;
  border: 1px solid rgba(127, 127, 127, 0.35);
  background: transparent;
  color: inherit;
  width: 100%;
  box-sizing: border-box;
}
.maicb-handoff-input:focus {
  outline: none;
  border-color: var(--maicb-primary);
}
.maicb-handoff-row {
  display: flex;
  gap: 8px;
}
.maicb-handoff-submit {
  font: inherit;
  font-size: 14px;
  padding: 7px 16px;
  border-radius: 8px;
  border: none;
  background: var(--maicb-primary);
  color: #fff;
  cursor: pointer;
}
.maicb-handoff-submit:disabled {
  opacity: 0.6;
  cursor: default;
}
.maicb-handoff-cancel {
  font: inherit;
  font-size: 14px;
  padding: 7px 12px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: inherit;
  opacity: 0.6;
  cursor: pointer;
}
.maicb-handoff-error {
  font-size: 12px;
  color: #DC2626;
  min-height: 0;
}
.maicb-citations {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: -2px 0 6px 12px;
  max-width: 85%;
}
.maicb-citations-label {
  font-size: 11px;
  opacity: 0.6;
  letter-spacing: 0.02em;
}
.maicb-citation {
  font-size: 11px;
  line-height: 1.4;
  padding: 2px 8px;
  border-radius: 10px;
  border: 1px solid currentColor;
  opacity: 0.75;
  text-decoration: none;
  color: inherit;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: opacity 0.15s ease;
}
.maicb-citation:hover {
  opacity: 1;
  text-decoration: underline;
}
.maicb-feedback {
      display: flex;
      align-items: center;
      gap: 2px;
      align-self: flex-start;
      margin-top: -4px;
    }
    .maicb-feedback-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border: none;
      background: transparent;
      color: var(--maicb-text); opacity: 0.6;
      cursor: pointer;
      border-radius: 6px;
      padding: 0;
    }
    .maicb-feedback-btn:hover { background: var(--maicb-input); color: var(--maicb-text); }
    .maicb-feedback-btn svg { width: 13px; height: 13px; }
    .maicb-feedback-btn.maicb-feedback-active-up { color: #16a34a; }
    .maicb-feedback-btn.maicb-feedback-active-down { color: #dc2626; }
    .maicb-feedback-btn:disabled { cursor: default; }

    /* Rich text inside bubbles. Margins are tight because a chat bubble is a
       small space default paragraph spacing looks broken here. */
    .maicb-para { margin: 0 0 6px; }
    .maicb-para:last-child { margin-bottom: 0; }

    .maicb-list {
      margin: 4px 0 6px;
      padding-left: 18px;
      display: flex;
      flex-direction: column;
      gap: 3px;
    }
    .maicb-list:last-child { margin-bottom: 0; }
    .maicb-list li { line-height: 1.45; }

    .maicb-code {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.9em;
      background: rgba(0, 0, 0, 0.06);
      border-radius: 4px;
      padding: 1px 4px;
      word-break: break-word;
    }
    .maicb-bubble-user .maicb-code { background: rgba(255, 255, 255, 0.2); }

    .maicb-bubble strong { font-weight: 600; }

    .maicb-link {
      color: var(--maicb-primary);
      text-decoration: underline;
      text-underline-offset: 2px;
      font-weight: 500;
      word-break: break-word;
    }
    .maicb-link:hover { text-decoration-thickness: 2px; }
    /* On the visitor's own bubble the background is the brand color, so the
       link has to invert to stay legible. */
    .maicb-bubble-user .maicb-link { color: inherit; }

    .maicb-counter {
      display: none;
      flex-shrink: 0;
      font-size: 10px;
      line-height: 1;
      color: rgba(0,0,0,0.4);
      font-variant-numeric: tabular-nums;
      pointer-events: none;
    }
    .maicb-counter-max { color: #dc2626; font-weight: 600; }

    .maicb-branding {
      display: flex;
      justify-content: center;
      padding: 6px 12px 8px;
      background: var(--maicb-surface);
      flex-shrink: 0;
    }
    /* [hidden] alone loses to display:flex, so it needs the override. */
    .maicb-branding[hidden] { display: none; }
    .maicb-branding-link {
      font-size: 11px;
      line-height: 1;
      color: var(--maicb-text);
      opacity: 0.5;
      text-decoration: none;
      transition: opacity 0.15s ease;
    }
    .maicb-branding-link:hover {
      opacity: 0.8;
      text-decoration: underline;
    }

    /* One-click prompts, between the transcript and the composer. They
       scroll horizontally rather than wrapping, so three or four never push
       the input out of a short panel. */
    .maicb-suggestions {
      display: flex;
      gap: 6px;
      padding: 0 12px 8px;
      overflow-x: auto;
      scrollbar-width: none;
      flex-shrink: 0;
    }
    .maicb-suggestions::-webkit-scrollbar { display: none; }
    .maicb-suggestion {
      font: inherit;
      font-size: 13px;
      line-height: 1.3;
      white-space: nowrap;
      padding: 7px 12px;
      border-radius: 9999px;
      border: 1px solid var(--maicb-border);
      background: var(--maicb-surface);
      color: var(--maicb-primary);
      cursor: pointer;
      transition: background 0.15s ease, border-color 0.15s ease;
    }
    .maicb-suggestion:hover {
      background: var(--maicb-bot-bubble);
      border-color: var(--maicb-primary);
    }

    .maicb-footer {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;
      border-top: 1px solid var(--maicb-border);
      background: var(--maicb-surface);
      flex-shrink: 0;
    }
    .maicb-input {
      flex: 1;
      border: none;
      outline: none;
      background: var(--maicb-input);
      border-radius: 9999px;
      padding: 12px 16px;
      /* Never below 16px: iOS Safari zooms the whole page when a focused
         input is smaller, which yanks the host site around on every tap. */
      font-size: 16px;
      color: var(--maicb-text);
      -webkit-appearance: none;
      -webkit-tap-highlight-color: transparent;
    }
    .maicb-input::placeholder { color: var(--maicb-text); opacity: 0.55; }
    .maicb-input:focus { background: var(--maicb-input); filter: brightness(0.96); }
    .maicb-send {
      flex-shrink: 0;
      width: 30px;
      height: 30px;
      border-radius: 9999px;
      background-color: var(--maicb-primary);
      color: white;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
    .maicb-send svg { width: 14px; height: 14px; }
    .maicb-send:disabled { opacity: 0.5; cursor: default; }

    .maicb-typing {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 8px 12px;
    }

    .maicb-typing-dot {
      width: 6px;
      height: 6px;
      background: currentColor;
      border-radius: 50%;
      animation: maicb-typing-bounce 1.4s infinite ease-in-out both;
    }

    .maicb-typing-dot:nth-child(1) { animation-delay: -0.32s; }
    .maicb-typing-dot:nth-child(2) { animation-delay: -0.16s; }

    @keyframes maicb-typing-bounce {
      0%, 80%, 100% { transform: scale(0); }
      40% { transform: scale(1); }
    }

    @media (max-width: 420px) {
      :host, .maicb-root {
        --maicb-offset-side: 12px;
        --maicb-offset-bottom: 16px;
      }
      .maicb-panel { 
        width: calc(100vw - 24px); 
        ${side}: 12px; 
        max-height: calc(100vh - 100px);
        bottom: 80px;
      }
      .maicb-launcher { ${side}: 12px; bottom: 16px; }
      .maicb-header { padding: 12px 14px; font-size: 13px; }
      .maicb-bubble { font-size: 15px; }
      /* Still 16px the iOS zoom threshold applies most on phones. */
      .maicb-input { font-size: 16px; }
    }

    @media (max-width: 375px) {
      :host, .maicb-root {
        --maicb-offset-side: 8px;
      }
      .maicb-panel { 
        width: calc(100vw - 16px); 
        ${side}: 8px; 
        border-radius: 12px;
      }
      .maicb-launcher { ${side}: 8px; width: 50px; height: 50px; }
      .maicb-header { padding: 10px 12px; font-size: 12px; }
      .maicb-messages { padding: 10px; }
      .maicb-footer { padding: 6px; }
      /* Too intrusive on a phone, where it would cover the content. */
      .maicb-greeting { display: none; }
    }
    
    ${RTL_STYLES}
    
    ${upgradeLegacyCss(customCss) || ''}
  `;
}

/**
 * Right-to-left overrides. Arabic reverses reading order, so bubbles, the
 * composer and the citation row all mirror not just the text direction.
 */
export const RTL_STYLES = `
  .maicb-root[dir="rtl"] .maicb-bubble-user { align-self: flex-start; }
  .maicb-root[dir="rtl"] .maicb-bubble-assistant { align-self: flex-end; }
  .maicb-root[dir="rtl"] .maicb-bubble-user {
    border-radius: 16px 16px 16px 4px;
  }
  .maicb-root[dir="rtl"] .maicb-bubble-assistant {
    border-radius: 16px 16px 4px 16px;
  }
  .maicb-root[dir="rtl"] .maicb-header { flex-direction: row-reverse; }
  .maicb-root[dir="rtl"] .maicb-footer { flex-direction: row-reverse; }
  .maicb-root[dir="rtl"] .maicb-input { text-align: right; }
  .maicb-root[dir="rtl"] .maicb-citations {
    flex-direction: row-reverse;
    margin: -2px 12px 6px 0;
  }
  .maicb-root[dir="rtl"] .maicb-feedback { flex-direction: row-reverse; }
  .maicb-root[dir="rtl"] .maicb-greeting { flex-direction: row-reverse; }
  .maicb-root[dir="rtl"] .maicb-prechat { direction: rtl; }
  .maicb-root[dir="rtl"] .maicb-handoff-form { direction: rtl; }
  .maicb-root[dir="rtl"] .maicb-handoff-offer { justify-content: flex-end; }
  .maicb-root[dir="rtl"] .maicb-send { transform: scaleX(-1); }
`;
